import {z} from 'zod';

import {
  CompositionPreviewApiResponse,
  CompositionPreviewResponseBody,
  CompositionSceneSchema,
  UuidResponseBody,
} from '@dms/api/shared';

// BE accepts any CSS weight 100..900; the editor UI offers 400 and 700 (matching the built-in
// Inter faces and the scene's text layer fontWeight).
export const CompositionFontWeightSchema = z.number().int().min(100).max(900);
export type CompositionFontWeight = z.infer<typeof CompositionFontWeightSchema>;

export const CompositionFontStyleSchema = z.enum(['normal', 'italic']);
export type CompositionFontStyle = z.infer<typeof CompositionFontStyleSchema>;

export type CompositionFontResponseBody = {
  id: string;
  family: string;
  weight: CompositionFontWeight;
  style: CompositionFontStyle;
  fileId: string;
  // Null when the font file is missing in storage; the font is then listed but cannot be loaded.
  url: string | null;
};
export type CompositionFont = CompositionFontResponseBody;

export type GetCompositionFontListApiResponse = /** status 200  */ CompositionFontResponseBody[];

export const CreateCompositionFontRequestBodySchema = z.object({
  family: z.string().min(1).max(255),
  weight: CompositionFontWeightSchema,
  style: CompositionFontStyleSchema,
  fileId: z.string(),
});
export type CreateCompositionFontRequestBody = z.infer<
  typeof CreateCompositionFontRequestBodySchema
>;
export type CreateCompositionFontApiArg = {
  createCompositionFontRequestBody: CreateCompositionFontRequestBody;
};
export type CreateCompositionFontApiResponse = /** status 201  */ UuidResponseBody;

export type DeleteCompositionFontApiResponse = unknown;
export type DeleteCompositionFontApiArg = {
  id: string;
};

export const CompositionPreviewRequestBodySchema = z.object({
  scene: CompositionSceneSchema,
});
export type CompositionPreviewRequestBody = z.infer<typeof CompositionPreviewRequestBodySchema>;
export type CompositionPreviewApiArg = {
  compositionPreviewRequestBody: CompositionPreviewRequestBody;
};
export type {CompositionPreviewApiResponse, CompositionPreviewResponseBody};

export const CompositionBadgeSizeSchema = z.enum(['256x256', '512x512', '1024x1024']);
export type CompositionBadgeSize = z.infer<typeof CompositionBadgeSizeSchema>;

export const CompositionBadgeBackgroundSchema = z.enum(['transparent', 'opaque']);
export type CompositionBadgeBackground = z.infer<typeof CompositionBadgeBackgroundSchema>;

export const CompositionBadgeStyleSchema = z.enum(['badge', 'illustration']);
export type CompositionBadgeStyle = z.infer<typeof CompositionBadgeStyleSchema>;

export const GenerateCompositionBadgeRequestBodySchema = z.object({
  prompt: z.string().min(1).max(1000),
  size: CompositionBadgeSizeSchema,
  background: CompositionBadgeBackgroundSchema,
  style: CompositionBadgeStyleSchema,
});
export type GenerateCompositionBadgeRequestBody = z.infer<
  typeof GenerateCompositionBadgeRequestBodySchema
>;
export type GenerateCompositionBadgeApiArg = {
  generateCompositionBadgeRequestBody: GenerateCompositionBadgeRequestBody;
};
// The generated PNG is stored as a regular file; the editor references it like an uploaded image.
export type CompositionBadgeResponseBody = {
  fileId: string;
  url: string;
};
export type GenerateCompositionBadgeApiResponse = /** status 201  */ CompositionBadgeResponseBody;
